import {React, useState, useEffect, useMemo} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "primereact/button";
import { ConfirmPopup } from 'primereact/confirmpopup';
import { Dialog } from "primereact/dialog";

import { raiseToast } from "../../../../services/redux/reducers/toast/toast";
import { addDaysToDate, getBuyerProductName, getBuyerProductSpecification, getDateString, getProductQty, numberConversion } from "../../../../utils";
import { getDocCopyByDocNo } from "../../../../services/api/docCopy";

import { getApprovalRequestStatusDetails, getBuyerPOApprovals } from "../../../../utils";
import downloadFile from "../../../../utils/DownloadFile";
import NewApprovalsDisplay from "../../../BuyerPO/Components/NewApprovalsDisplay";

import { getPMCompanyConfig } from "../../../../setup/pmCompanyConfig";
import FileViewModal from "../../../../common/FileViewModal";


const BuyerPOModalData = (props) => {
    const { buyerPO, buyerQuote, buyerReq, buyerPOItems, materialDetails, approvalInstances, approvals, approvalTypes, handleApprovalRequest } = props

    const [isPageLoading, setIsPageLoading] = useState(false)
    const [visibleApprovals, setVisibleApprovals] = useState(false)
    const [approvalRequestStatusDetails, setApprovalRequestStatusDetails] = useState([])
    const [buyerPODocument, setBuyerPODocument] = useState('')
    const [visibleDocument, setVisibleDocument] = useState(false)

    const dispatch = useDispatch()

    const pmCompanyConfig = getPMCompanyConfig(buyerPO.pmCompanyId)
    let { currencyShortName, numberLocale } = pmCompanyConfig
    
    const approvalSecondary = useSelector((state) => state.common.approvalSecondary)

    let POApprovals = useMemo(() => {
        return approvals?.filter(eachApproval => pmCompanyConfig.buyerPOApprovalTypeIds.includes(eachApproval.approvalTypeId))
    }, [approvals])

    useEffect(() => {
        if(buyerPO && buyerPOItems.length > 0){
            calculateApprovals()
        }
    }, [buyerPO, buyerPOItems, approvalInstances])

    const viewBuyerPOPDF = async () => {
        setIsPageLoading(true)

        const docCopyRes = await getDocCopyByDocNo(buyerPO.docNo, true)
        if (docCopyRes.failure){
            return dispatch(raiseToast({ severity: 'error', detail: docCopyRes?.data?.error }))
        }
        setIsPageLoading(false)

        // window.open(docCopyRes.docLocation, "_blank")
        setVisibleDocument(true)
        setBuyerPODocument(docCopyRes.docLocation)
    }

    const downloadBuyerPOPDF = async () => {
        setIsPageLoading(true)

        const docCopyRes = await getDocCopyByDocNo(buyerPO.docNo, true)
        if (docCopyRes.failure){
            return dispatch(raiseToast({ severity: 'error', detail: docCopyRes?.data?.error }))
        }
        setIsPageLoading(false)

        downloadFile(docCopyRes.docLocation, `PO-${buyerPO.docNo}.pdf`)
    }

    const handleRemarks = (srNo, text) => {
        const updateRequiredApprovals = approvalRequestStatusDetails.map(eachApproval => {
            if(eachApproval.srNo === srNo){
                return {...eachApproval, remarks: text}
            }
            return eachApproval
        })

        setApprovalRequestStatusDetails(updateRequiredApprovals)
    }

    const calculateApprovals = () => {
        const updatedRequiredApprovals = getBuyerPOApprovals({buyerPO, buyerPOItems, POApprovals, buyerReq})

        const pmBranchId = buyerPO.pmBranchId
        const approvalRequestStatusDetails = getApprovalRequestStatusDetails({updatedRequiredApprovals, approvalInstances, approvals, pmBranchId, buyerPO})
        setApprovalRequestStatusDetails(approvalRequestStatusDetails)
    }

    const handleBuyerQuoteView = () => {
        setVisibleDocument(false)
        setBuyerPODocument('')
    }

    let pendingApprovalsExist = false
    let allApprovalsApproved = false

    if(approvalRequestStatusDetails?.length){
        const pendingApprovalStatusDetails = approvalRequestStatusDetails.filter(eachReq => ["NOT REQUESTED", "REQUESTED", "REJECTED"].includes(eachReq.requestStatus))
        const approvedApprovalStatusDetails = approvalRequestStatusDetails.filter(eachReq => eachReq.requestStatus === "APPROVED")

        if(pendingApprovalStatusDetails.length){
            pendingApprovalsExist = true
        }
        if(approvedApprovalStatusDetails.length === approvalRequestStatusDetails.length){
            allApprovalsApproved = true
        }
    }else{
        allApprovalsApproved = true
    }

    return (
        <div className={`p-card my-2 py-2 buyerpo-modal-data ${buyerPO?.statusId === 6 ? 'cancelled-watermark' : ''} ${buyerPO?.statusId === 4 ? 'force-closed-watermark' : ''} `} style={{overflowX: 'hidden'}}>
            <ConfirmPopup />
            <FileViewModal visible={visibleDocument} document={buyerPODocument} handleOnHide={handleBuyerQuoteView} docName={buyerQuote.docNo} />

            <div className="grid">
                <div className="flex flex-row col-12 justify-content-between pb-0">
                    <div className="flex flex-row justify-content-start align-items-center col-12 md:col-6 lg:col-8 lg:mb-0 pl-0">
                        <h5 className="m-0 quote-number doc-no-navigation mr-4"> <span style={{ fontWeight: "lighter" }}>PO No:</span> {buyerPO?.docNo}</h5>
                        {
                            buyerPO.clientPONum ? 
                                <>
                                    <a className="m-0 quote-number doc-no-navigation" style={{color: "black", fontWeight: "bold"}} href={buyerPO.docProof} target="_blank"> 
                                        <span style={{ fontWeight: "lighter" }}>Client PO No: <i className="pi pi-external-link pl-1 doc-copy-icon"></i> </span> 
                                        <span>{buyerPO.clientPONum}</span>
                                    </a> 
                                </>
                            : ""
                        }
                        {buyerPO ? <span className="ml-4">PO Received Date: <span className="font-semibold">{buyerPO.poReceivedDate ? getDateString(buyerPO.poReceivedDate) : getDateString(buyerPO.createdAt) }</span></span> : ""}
                        {buyerPO ? <span className="ml-4">PO Doc Date: <span className="font-semibold">{buyerPO.poDocDate ? getDateString(buyerPO.poDocDate) : getDateString(buyerPO.createdAt) }</span></span> : ""}
                    </div>
                    {
                        allApprovalsApproved &&
                        <div className="flex flex-row col-12 mb-2  md:col-4 lg:col-4 lg:mb-0 align-items-end justify-content-end pt-0">
                            <Button severity="success" rounded onClick={viewBuyerPOPDF}>
                                <i className="pi pi-eye" style={{ fontSize: "0.9rem" }} />
                            </Button>
                            <Button className="bg-blue-300 border-blue-300" severity="success ml-2" rounded onClick={downloadBuyerPOPDF}>
                                <i className="pi pi-download" style={{ fontSize: "0.9rem" }} />
                            </Button>
                        </div>
                    }
                    {
                        pendingApprovalsExist && (
                            <div className="flex flex-column col-12 mb-2 md:col-4 lg:col-4 lg:mb-0 align-items-center justify-content-center">
                                <div>
                                    <span style={{color: "red"}}>Approval Pending</span>
                                    <span className="ml-2" onClick={() => setVisibleApprovals(true)} style={{cursor: "pointer", fontSize: "12px", textDecoration: "underline"}}>View Details</span>
                                </div>
                            </div>
                        )
                    }
                    <div className="flex flex-row col-12 mb-2  md:col-4 lg:col-4 lg:mb-0 align-items-center justify-content-end">
                        {/* <Button severity="success" rounded onClick={() => navigate(`/buyerpo/${buyerPO?.buyerPOId}`)}><i className="pi pi-eye" style={{ fontSize: "0.9rem" }} /></Button>
                        <Button severity={buyerPO?.statusId === 3 ? "danger" : "secondary"} disabled={buyerPO?.statusId === 6} loading={buyerPODeleteLoading} className="ml-2" permissions={[14]} rounded onClick={(event) => handleDelete(event)}>
                            {
                                !buyerPODeleteLoading ? <i className="pi pi-trash align-items-center" style={{ fontSize: "0.9rem", cursor: 'pointer'  }} /> : null
                            }
                        </Button> */}
                    </div>
                </div>
            </div>
            <div className="dialog-table-container">
                <table className="dialog-table" style={{ width: "100%" }}>
                    <thead >
                        <tr className="">
                            <th className="" style={{ width: "5%"}}>SR</th>
                            <th className="" style={{ width: "20%"}}>PRODUCT</th>
                            <th className="" style={{ width: "35%"}}>SPECIFICATION</th>
                            <th className="" style={{ width: "10%"}}>DELIVERED BY</th>
                            <th className="" style={{ width: "10%"}}>QUANTITY</th>
                            <th className="" style={{ width: "10%"}}>UNITPRICE</th>
                            <th className="" style={{ width: "10%"}}>{pmCompanyConfig.GSTRateLabel}</th>
                            <th className="" style={{ width: "10%"}}>AMOUNT</th>
                        </tr>
                    </thead>
                    <tbody style={{ fontSize: "12px", borderCollapse: "collapse" }}>
                        {
                            buyerPOItems?.map((eachBuyerPOItem, index) => {
                                const relatedMaterial = materialDetails?.find(eachMaterial => eachMaterial?.materialId === eachBuyerPOItem?.materialId)
                                let deliveredByDate = null
                                if (buyerPO.poReceivedDate){
                                    deliveredByDate = new Date(buyerPO.poReceivedDate)
                                }else {
                                    deliveredByDate = new Date(buyerPO.createdAt)
                                }
                                deliveredByDate = addDaysToDate(deliveredByDate, parseInt(eachBuyerPOItem.leadTime))
                                deliveredByDate = getDateString(deliveredByDate)
                                
                                return (
                                    <tr key={`buyerPOItem-${eachBuyerPOItem.buyerPOItemId}`} className="" style={{ borderBottom: "1px solid #818181", borderCollapse: "collapse" }}>
                                        <td className="" style={{ width: "5%"}}>{index + 1}</td>
                                        <td className="" style={{ width: "20%"}}>{getBuyerProductName(relatedMaterial.companyProdName, relatedMaterial.productName)}</td>
                                        <td className="" style={{ width: "35%"}}>{getBuyerProductSpecification(relatedMaterial?.companyProdSpec, relatedMaterial?.productSpecification, relatedMaterial.UOM, relatedMaterial.UOMValue)}</td>
                                        <td className="" style={{ width: "10%"}}>{deliveredByDate}</td>
                                        <td className="" style={{ width: "10%"}}>{getProductQty(eachBuyerPOItem?.bPOQty, relatedMaterial.UOM, relatedMaterial.UOMValue)}</td>
                                        <td className="" style={{ width: "10%"}}>{currencyShortName}.{numberConversion(eachBuyerPOItem?.bPOUnitPrice, numberLocale)}</td>
                                        <td className="" style={{ width: "10%"}}>{eachBuyerPOItem?.bPOGSTRate}%</td>
                                        <td className="" style={{ width: "10%"}}>{numberConversion(eachBuyerPOItem?.bPOItemAmountWithTax, numberLocale)}</td>
                                    </tr>
                                )
                            })
                        }
                    </tbody>
                </table>
            </div>
            <div className="flex flex-row justify-content-end col-12 mb-2 lg:mb-0 ">
                <div><span>Total Price:</span>&nbsp;<span className="price-value font-semibold">{numberConversion(parseFloat(buyerPO?.bPOTotalAmountWithTax), numberLocale)}</span></div>
            </div>

            <Dialog header="Approvals" className="approvals-dialog" visible={visibleApprovals} onHide={() => setVisibleApprovals(false)} style={{ width: '80vw',height: '80vh' }} >
                {/* <ApprovalsDisplay
                    buyerPO={buyerPO}
                    buyerPOItems={buyerPOItems}
                    approvalInstances={approvalInstances}
                    POApprovals={POApprovals}
                    approvalSecondary={approvalSecondary}
                    materialDetails={materialDetails}
                    approvalRequestStatusDetails={approvalRequestStatusDetails}
                    setApprovalRequestStatusDetails={setApprovalRequestStatusDetails}
                    buyerPOId={buyerPO.buyerPOId}
                    buyerReq={buyerReq}
                    buyerQuote={buyerQuote}
                    isDoc={true}
                    handleRemarks={handleRemarks}
                    handleApprovalRequest={handleApprovalRequest}
                    buyerSideFlag={true}
                /> */}
                <NewApprovalsDisplay
                    buyerPO={buyerPO}
                    buyerPOItems={buyerPOItems}
                    approvalTypes={approvalTypes}
                    approvalInstances={approvalInstances}
                    POApprovals={POApprovals}
                    approvalSecondary={approvalSecondary}
                    materialDetails={materialDetails}
                    approvalRequestStatusDetails={approvalRequestStatusDetails}
                    setApprovalRequestStatusDetails={setApprovalRequestStatusDetails}
                    buyerPOId={buyerPO.buyerPOId}
                    buyerReq={buyerReq}
                    buyerQuote={buyerQuote}
                    isDoc={true}
                    handleRemarks={handleRemarks}
                    handleApprovalRequest={handleApprovalRequest}
                    buyerSideFlag={true}
                />
            </Dialog>
        </div>
    )
}

export default BuyerPOModalData;